import { useEffect } from 'react'

import { type Step } from '../utils/steps'

import { useStep } from './use-step'

/**
 * Detects the and sets the step based on the scroll position of the main page
 * @param mainPageRef - The ref to the main page
 * @returns The current step
 */
export const useStepWithScrollDetection = (mainPageRef: React.RefObject<HTMLDivElement | null>) => {
  const { step, setStep } = useStep()

  // Handle step detection using Intersection Observer
  useEffect(() => {
    const container = mainPageRef.current
    if (!container) return

    const stepElements = document.querySelectorAll(`[data-step]`) || []
    if (stepElements.length === 0) return

    // Create intersection observer
    const observer = new IntersectionObserver(
      entries => {
        // Find the entry with the highest intersection ratio
        let mostVisibleEntry = entries[0]
        let maxRatio = 0

        for (const entry of entries) {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio
            mostVisibleEntry = entry
          }
        }

        // Only update if we have a visible element and it's different from current step
        if (mostVisibleEntry && mostVisibleEntry.intersectionRatio > 0) {
          const targetElement = mostVisibleEntry.target as HTMLElement
          const stepValue = targetElement.dataset.step as Step

          if (stepValue && stepValue !== step) {
            setStep(stepValue)
          }
        }
      },
      {
        root: container, // Use the main page container as the root
        rootMargin: '-20% 0px -20% 0px', // Only trigger when element is in the center 60% of viewport
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1], // Multiple thresholds for smooth detection
      },
    )

    // Observe all step elements
    for (const element of stepElements) {
      observer.observe(element)
    }

    return () => {
      observer.disconnect()
    }
  }, [setStep, mainPageRef, step])

  return { step }
}
